import { Dispatch, SetStateAction } from "react";
import { SystemEntityCollections } from "../shared/enums/shared-enums";

export const extractKeyFromArangoId = (id?: string): string => {
  if (!id || !id.includes("/")) return id ?? "";
  return id.split("/")[1];
};

export const extractCollectionFromArangoId = (
  id?: string,
): SystemEntityCollections | null => {
  if (!id) return null;
  const collection = id.includes("/") ? id.split("/")[0] : id;
  return (Object.values(SystemEntityCollections) as string[]).includes(
    collection,
  )
    ? (collection as SystemEntityCollections)
    : null;
};

export function createRowsFieldHandler<
  Row extends Record<string, any>,
  Key extends keyof Row,
>(
  prop: Key,
  onChange: (args: { name: string; value: Row[Key][] }) => void,
  fieldName?: string, // ← only needed when prop≠form‑field
) {
  const name = fieldName ?? (prop as string);
  return (_: string, rows: Row[]) => {
    const values = rows.map((r) => r[prop]);
    onChange({ name, value: values });
  };
}

export const formatFieldName = (name: string) => {
  // Replace underscores with spaces and split camelCase
  const formattedName = name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

  // Capitalize first letter of each word
  return formattedName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Create a push handler that updates state and calls onChange
export const createUpdateItemAndNotify = <T,>(
  setState: Dispatch<SetStateAction<T>>,
  onChange?: (item: T) => void,
) => {
  return (updatedItem: T) => {
    setState(updatedItem);
    onChange?.(updatedItem);
  };
};

// Create a wrapped version change handler
export const createVersionChangeWrapperHandler = <
  T extends {
    version_exist?: boolean;
    version_id?: string;
    version_notes?: string;
  },
>(
  item: T,
  updateItemAndNotify: (updated: T) => void,
  handleVersionChange: (
    versionExists: boolean,
    version?: any,
    versionNotes?: string,
  ) => void,
) => {
  return (
    versionExists: boolean = false,
    version?: any,
    versionNotes: string = "",
  ) => {
    const updated: T = {
      ...item,
      version_exist: versionExists,
      version_id: version?._key || null,
      version_notes: versionNotes,
    };
    updateItemAndNotify(updated);
    handleVersionChange(versionExists, version, versionNotes);
  };
};

export const buildEditTitleForWork = (
  publishedId: string | undefined | null,
  name: string | undefined | null,
  systemLanguageLabel: string | undefined,
  isCreateMode: boolean,
): string => {
  const prefix = isCreateMode ? "Create New" : "Edit";
  const workType = systemLanguageLabel ? systemLanguageLabel.toLowerCase() : "work";
  const publishedIdTitle = `${prefix} ${workType} work: ${publishedId || ""}`;
  return name ? `${publishedIdTitle}\n${name}` : publishedIdTitle;
};

interface UserInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  isStatic?: boolean;
}

export const UserInput: React.FC<UserInputProps> = ({
  value,
  onChange,
  disabled = false,
  isStatic = false,
}) => {
  return isStatic ? (
    <p className="static-field">{value}</p>
  ) : (
    <input
      type="text"
      id="name"
      name="name"
      className="form-input"
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
};
